import { AxisBottom, AxisLeft } from '@visx/axis'
import { localPoint } from '@visx/event'
import { LinearGradient } from '@visx/gradient'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { useParentSize } from '@visx/responsive'
import { scaleLinear } from '@visx/scale'
import { AreaClosed, Bar } from '@visx/shape'
import { TooltipWithBounds, defaultStyles, useTooltip } from '@visx/tooltip'
import type React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useWorkoutMetricsGroup } from '../../hooks/useWorkoutMetrics'
import { theme } from '../../theme/theme'
import type { CalculatedZone } from '../../types/zones'
import { formatPace, formatTimeSeconds } from '../../utils/dateUtils'

interface SplitsChartProps {
  workoutId: string
  hrZones?: CalculatedZone[]
}

interface MetricSample {
  timestamp: number
  value: number
}

interface Split {
  splitNumber: number
  distance: number
  duration: number
  ascent: number
  descent: number
  avgHeartRate: number
  pace: number // min/km
  startTime: number
  endTime: number
  hrZone: CalculatedZone | null
  hrZoneColor: string | null
}

const Container = styled.div`
  width: 100%;
  position: relative;
`

const LoadingMessage = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
`

const MetricValue = styled.span`
  font-weight: 700;
`

const tooltipStyles = {
  ...defaultStyles,
  background: theme.colors.cardBackground,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: theme.borderRadius.sm,
  padding: theme.spacing.sm,
  fontSize: theme.typography.xs,
  color: theme.colors.text,
}

const getHRZone = (avgHR: number, hrZones?: CalculatedZone[]): CalculatedZone | null => {
  if (!hrZones || hrZones.length === 0) {
    return null
  }

  // Sort to ensure we evaluate zones in ascending order
  const sortedZones = [...hrZones].sort(
    (a, b) => (a.minValue ?? Number.NEGATIVE_INFINITY) - (b.minValue ?? Number.NEGATIVE_INFINITY)
  )

  for (const [index, zone] of sortedZones.entries()) {
    const min = zone.minValue ?? Number.NEGATIVE_INFINITY
    // Treat a missing max on the final zone as unbounded so values above the top limit still match
    const max = zone.maxValue ?? (index === sortedZones.length - 1 ? Number.POSITIVE_INFINITY : null)
    if (max !== null && avgHR >= min && avgHR <= max) {
      return zone
    }
  }

  // If above all configured ranges, map to the highest zone
  const topZone = sortedZones[sortedZones.length - 1]
  if (avgHR > (topZone.maxValue ?? Number.POSITIVE_INFINITY)) {
    return topZone
  }

  return null
}

// Tooltip content
const TooltipContent = ({ split }: { split: Split }) => (
  <div>
    <div style={{ fontWeight: 600, marginBottom: theme.spacing.xs }}>Split {split.splitNumber}</div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Distance:</span>
      <MetricValue>{split.distance} km</MetricValue>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Ascent/Descent:</span>
      <MetricValue>
        {split.ascent.toFixed(0)} m / {split.descent.toFixed(0)} m
      </MetricValue>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Avg Pace:</span>
      <MetricValue>{formatPace(split.pace)} /km</MetricValue>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Avg Heart Rate:</span>
      <MetricValue>{Math.round(split.avgHeartRate)} bpm</MetricValue>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: theme.spacing.md, marginBottom: '4px' }}>
      <span style={{ color: theme.colors.textSecondary }}>Heart Rate Zone:</span>
      <MetricValue>{split.hrZone?.name}</MetricValue>
    </div>
  </div>
)

export const SplitsChart: React.FC<SplitsChartProps> = ({ workoutId, hrZones }) => {
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<Split>()
  const { parentRef, width } = useParentSize({ debounceTime: 150 })
  const [splits, setSplits] = useState<Split[]>([])
  const [altitudeData, setAltitudeData] = useState<{ timestamp: number; value: number }[]>([])
  const [distanceData, setDistanceData] = useState<{ timestamp: number; value: number }[]>([])
  const [hrData, setHrData] = useState<{ timestamp: number; value: number }[]>([])
  const { data: metrics, isLoading } = useWorkoutMetricsGroup(workoutId, [
    {
      name: 'distance',
      downsampleInterval: 1,
      smoothingWindow: 1,
      color: '',
      unit: '',
      label: '',
    },
    {
      name: 'heart_rate',
      downsampleInterval: 1,
      smoothingWindow: 1,
      color: '',
      unit: 'bpm',
      label: 'HR',
    },
    {
      name: 'altitude',
      downsampleInterval: 1,
      smoothingWindow: 1,
      color: '',
      unit: 'm',
      label: 'Altitude',
    },
  ])

  useEffect(() => {
    if (!metrics) return
    const [distanceResult, hrResult, altitudeResult] = metrics
    const distanceData = (distanceResult.samples || []) as MetricSample[]
    setDistanceData(distanceData)

    const hrData = (hrResult.samples || []) as MetricSample[]
    setHrData(hrData)

    const altitudeDataRaw = (altitudeResult.samples || []) as MetricSample[]
    setAltitudeData(altitudeDataRaw)
  }, [metrics])

  // Fetch metrics data and calculate splits
  useEffect(() => {
    const loadSplitsData = async () => {
      if (!workoutId) return
      if (!distanceData.length) return

      // Calculate 1km splits
      const calculatedSplits: Split[] = []
      let splitNumber = 1
      const splitDistance = 1000 // 1km in meters

      // Create a map of HR values by timestamp for quick lookup
      const hrMap = new Map<number, number>()
      for (const sample of hrData) {
        hrMap.set(sample.timestamp, sample.value)
      }

      let splitStartIndex = 0
      let splitStartDistance = 0
      let splitStartTime = distanceData[0].timestamp

      for (let i = 0; i < distanceData.length; i++) {
        const currentDistance = distanceData[i].value
        const distanceSinceSplitStart = currentDistance - splitStartDistance

        // Check if we've completed a split
        if (distanceSinceSplitStart >= splitDistance) {
          const splitEndTime = distanceData[i].timestamp
          const splitDuration = splitEndTime - splitStartTime

          // calculate ascent and descent
          let ascent = 0
          let descent = 0
          for (let j = splitStartIndex; j <= i; j++) {
            if (j < 1) continue
            const prevAltitude = altitudeData[j - 1]?.value ?? 0
            const altitude = altitudeData[j]?.value ?? 0
            const heightDiff = altitude - prevAltitude
            if (heightDiff > 0) ascent += Math.abs(heightDiff)
            if (heightDiff < 0) descent += Math.abs(heightDiff)
          }

          // Calculate average heart rate for this split
          let hrSum = 0
          let hrCount = 0
          for (let j = splitStartIndex; j <= i; j++) {
            const hr = hrMap.get(distanceData[j].timestamp)
            if (hr !== undefined && hr > 0) {
              hrSum += hr
              hrCount++
            }
          }
          const avgHeartRate = hrCount > 0 ? hrSum / hrCount : 0

          // Calculate pace (min/km)
          const pace = splitDuration > 0 ? splitDuration / 60 : 0 // duration is in seconds, convert to minutes

          // Get HR zone color
          const hrZone = getHRZone(Math.round(avgHeartRate), hrZones)

          calculatedSplits.push({
            splitNumber,
            distance: splitNumber, // Distance in km (1, 2, 3, ...)
            duration: splitDuration,
            ascent,
            descent,
            avgHeartRate,
            pace,
            startTime: splitStartTime,
            endTime: splitEndTime,
            hrZone,
            hrZoneColor: hrZone?.color ?? null,
          })

          // Move to next split
          splitNumber++
          splitStartIndex = i
          splitStartDistance = currentDistance
          splitStartTime = splitEndTime
        }
      }
      setSplits(calculatedSplits)
    }
    loadSplitsData()
  }, [workoutId, hrZones, altitudeData, distanceData, hrData])

  // Render loading state
  const renderContent = () => {
    if (isLoading) {
      return <LoadingMessage>Loading splits data...</LoadingMessage>
    }

    if (splits.length === 0) {
      return <LoadingMessage>No split data available.</LoadingMessage>
    }

    // Wait for valid width before rendering chart
    if (!width || width === 0) {
      return null
    }

    // Chart dimensions
    const height = 250
    const margin = { top: 20, right: 20, bottom: 40, left: 60 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    // Calculate the time range from the splits
    const minTime = splits.length > 0 ? splits[0].startTime : 0
    const maxTime = splits.length > 0 ? splits[splits.length - 1].endTime : 0

    // Scales
    const xScale = scaleLinear({
      domain: [minTime, maxTime],
      range: [0, innerWidth],
    })

    // Calculate pace range (excluding any zero or invalid paces)
    const validPaces = splits.filter((s) => s.pace > 0).map((s) => s.pace)
    const minPace = Math.min(...validPaces)
    const maxPace = Math.max(...validPaces)
    const pacePadding = Math.max((maxPace - minPace) * 0.1, 0.2)

    // Invert domain so faster pace (lower values) appears higher
    const yScale = scaleLinear({
      domain: [maxPace + pacePadding, Math.max(0, minPace - pacePadding)],
      range: [innerHeight, 0],
    })

    // Altitude scale (for background area)
    let altitudeScale: any = null
    if (altitudeData.length > 0) {
      const altitudeValues = altitudeData.map((d) => d.value)
      const minAltitude = Math.min(...altitudeValues)
      const maxAltitude = Math.max(...altitudeValues)
      const altitudePadding = (maxAltitude - minAltitude) * 0.1

      altitudeScale = scaleLinear({
        domain: [minAltitude - altitudePadding, maxAltitude + altitudePadding],
        range: [innerHeight, 0],
      })
    }

    // Gap between bars for visual separation
    const gapSize = 2

    return (
      <>
        {/** biome-ignore lint/a11y/noSvgWithoutTitle: title conflicts with tooltip */}
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
          {/* Altitude gradient definition */}
          {altitudeData.length > 0 && (
            <LinearGradient
              id="splits-altitude-gradient"
              from="#9CA3AF"
              to="#9CA3AF"
              fromOpacity={0.5}
              toOpacity={0.2}
              vertical
            />
          )}

          <Group left={margin.left} top={margin.top}>
            {/* Altitude area in background */}
            {altitudeData.length > 0 && altitudeScale && (
              <AreaClosed
                data={altitudeData}
                x={(d) => xScale(d.timestamp)}
                y={(d) => altitudeScale(d.value)}
                yScale={altitudeScale}
                fill="url(#splits-altitude-gradient)"
                stroke="#9CA3AF"
                strokeWidth={0}
              />
            )}

            {/* Grid */}
            <GridRows scale={yScale} width={innerWidth} stroke="#E5E7EB" strokeOpacity={0.5} />

            {/* Bars for each split */}
            {splits.map((split) => {
              const barX = xScale(split.startTime) + gapSize
              const barWidth = Math.max(xScale(split.endTime) - xScale(split.startTime) - gapSize * 2, 1)
              const barHeight = innerHeight - yScale(split.pace)
              const barY = yScale(split.pace)

              return (
                <Bar
                  key={split.splitNumber}
                  x={barX}
                  y={barY}
                  width={barWidth}
                  height={barHeight}
                  fill={`${split.hrZoneColor}CC` || '#71CCAD'}
                  rx={4}
                  style={{ cursor: 'pointer' }}
                  onMouseMove={(event) => {
                    const point = localPoint(event)
                    showTooltip({
                      tooltipData: split,
                      tooltipLeft: point?.x,
                      tooltipTop: point?.y,
                    })
                  }}
                  onMouseLeave={() => {
                    hideTooltip()
                  }}
                />
              )
            })}

            {/* Axes */}
            <AxisBottom
              top={innerHeight}
              scale={xScale}
              stroke={theme.colors.textSecondary}
              tickStroke={theme.colors.textSecondary}
              numTicks={5}
              tickFormat={(value) => formatTimeSeconds(Number(value))}
              tickLabelProps={() => ({
                fill: theme.colors.textSecondary,
                fontSize: 10,
                textAnchor: 'middle',
              })}
              label="Time"
              labelProps={{
                fill: theme.colors.text,
                fontSize: 12,
                textAnchor: 'middle',
              }}
            />
            <AxisLeft
              scale={yScale}
              stroke={theme.colors.textSecondary}
              tickStroke={theme.colors.textSecondary}
              tickFormat={(value) => formatPace(Number(value))}
              tickLabelProps={() => ({
                fill: theme.colors.textSecondary,
                fontSize: 10,
                textAnchor: 'end',
                dx: -4,
              })}
              label="Pace (min/km)"
              labelProps={{
                fill: theme.colors.text,
                fontSize: 12,
                textAnchor: 'middle',
              }}
            />
          </Group>
        </svg>

        {/* Tooltip */}
        {tooltipOpen && tooltipData && (
          <TooltipWithBounds top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
            <TooltipContent split={tooltipData} />
          </TooltipWithBounds>
        )}
      </>
    )
  }

  return (
    <Container ref={parentRef} style={{ minHeight: '250px' }}>
      {renderContent()}
    </Container>
  )
}
